import { DEFAULT_CURRENCY } from "@/lib/format/money";
import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";
import {
  costBoolean,
  costDate,
  costNullableNumber,
  costNullableText,
  costNumber,
  costText,
  oneOf,
  toRemoteAmount,
  toRemoteFlag,
} from "@/lib/api/cost-fields";
import type { Cost, CostLogAction, CostLogEntry, CostStatus } from "@/types/fleet";

const COST_STATUSES = [
  "planned",
  "in_progress",
  "done",
  "cancelled",
] as const satisfies readonly CostStatus[];

const COST_LOG_ACTIONS = [
  "create",
  "update",
  "delete",
  "generate",
  "complete",
  "close",
  "unlock",
  "attachment_add",
  "attachment_delete",
] as const satisfies readonly CostLogAction[];

interface RemoteCost {
  id: string;
  client_device_id?: string;
  cost_category_id?: string;
  cost_date?: string;
  amount?: string;
  currency?: string;
  title?: string;
  description?: string;
  odometer?: string | null;
  is_planned?: string;
  status?: string;
  vendor?: string;
  invoice_no?: string;
  document_url?: string;
  cost_recurrence_id?: string | null;
  created_by?: string | null;
  created_by_name?: string;
  created_at?: string;
  updated_by?: string | null;
  updated_by_name?: string;
  updated_at?: string;
  /** A JSON number, unlike the rest of this record. Counts finished uploads only. */
  attachment_count?: number | string;
}

export interface RemoteCostLogEntry {
  id: string;
  cost_id?: string | null;
  cost_recurrence_id?: string | null;
  action?: string;
  user_id?: string | null;
  user_name?: string;
  created_at?: string;
  changes?: Record<string, { from?: string | null; to?: string | null }> | null;
}

/** Locked against edits, deletes and attachment changes for everyone but an admin's unlock. */
export function isCostLocked(cost: Pick<Cost, "status">): boolean {
  return cost.status === "done";
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

export function mapCost(cost: RemoteCost): Cost {
  return {
    id: cost.id,
    vehicleId: costText(cost.client_device_id),
    categoryId: costText(cost.cost_category_id),
    date: costDate(cost.cost_date),
    amount: costNullableNumber(cost.amount),
    currency: costText(cost.currency) || DEFAULT_CURRENCY,
    title: costText(cost.title),
    description: costText(cost.description),
    odometer: costNullableNumber(cost.odometer),
    isPlanned: costBoolean(cost.is_planned),
    status: oneOf(cost.status, COST_STATUSES, "done"),
    vendor: costText(cost.vendor),
    invoiceNo: costText(cost.invoice_no),
    documentUrl: costText(cost.document_url),
    recurrenceId: costNullableText(cost.cost_recurrence_id),
    createdBy: costText(cost.created_by_name),
    createdAt: costDate(cost.created_at),
    updatedBy: costText(cost.updated_by_name),
    updatedAt: costDate(cost.updated_at),
    attachmentCount: costNumber(cost.attachment_count),
  };
}

/** Shared with the recurrence log, which returns the same entry shape. */
export function mapCostLogEntry(entry: RemoteCostLogEntry): CostLogEntry {
  const changes: CostLogEntry["changes"] = {};
  for (const [field, change] of Object.entries(entry.changes ?? {})) {
    changes[field] = { from: change?.from ?? null, to: change?.to ?? null };
  }
  return {
    id: entry.id,
    costId: costNullableText(entry.cost_id),
    recurrenceId: costNullableText(entry.cost_recurrence_id),
    action: oneOf(entry.action, COST_LOG_ACTIONS, "update"),
    userId: costNullableText(entry.user_id),
    userName: costText(entry.user_name),
    timestamp: costDate(entry.created_at),
    changes,
  };
}

/**
 * The filters `/api/costs` forwards upstream. Every one is optional; omitting all of them asks
 * for the client's whole cost history, which is why the views always pass at least a date range.
 */
export interface CostFilters {
  /** `YYYY-MM-DD`, inclusive. */
  dateFrom?: string;
  dateTo?: string;
  vehicleIds?: string[];
  categoryIds?: string[];
  status?: CostStatus;
  isPlanned?: boolean;
}

/** Only the keys the proxy handler allows through; anything else upstream ignores anyway. */
export function costFiltersToQuery(filters: CostFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.vehicleIds?.length) {
    params.set("client_device_id", filters.vehicleIds.join(","));
  }
  if (filters.categoryIds?.length) {
    params.set("cost_category_id", filters.categoryIds.join(","));
  }
  if (filters.status) params.set("status", filters.status);
  if (filters.isPlanned !== undefined) {
    params.set("is_planned", toRemoteFlag(filters.isPlanned));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export interface CostInput {
  vehicleId: string;
  categoryId: string;
  date: string;
  /** Null is allowed only for an `in_progress` cost, whose price is not known yet. */
  amount: number | null;
  currency?: string;
  title?: string;
  description?: string;
  odometer?: number | null;
  isPlanned?: boolean;
  status?: CostStatus;
  vendor?: string;
  invoiceNo?: string;
  documentUrl?: string;
  /**
   * Set only when the app materialises a due reminder the generator has not created yet
   * (`docs/COSTS_API_SERVER.md`, "Actions on an item"). Normally the generator sets it.
   */
  recurrenceId?: string | null;
}

function toRemoteCost(input: Partial<CostInput>) {
  return {
    ...(input.vehicleId !== undefined ? { client_device_id: input.vehicleId } : {}),
    // An `in_progress` cost may not have a category yet; upstream wants null, not "".
    ...(input.categoryId !== undefined ? { cost_category_id: input.categoryId || null } : {}),
    ...(input.date !== undefined ? { cost_date: input.date } : {}),
    ...(input.amount !== undefined
      ? { amount: input.amount === null ? null : toRemoteAmount(input.amount) }
      : {}),
    ...(input.currency !== undefined ? { currency: input.currency } : {}),
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    // Null clears the reading upstream; undefined leaves it untouched.
    ...(input.odometer !== undefined
      ? { odometer: input.odometer === null ? "" : String(input.odometer) }
      : {}),
    ...(input.isPlanned !== undefined ? { is_planned: toRemoteFlag(input.isPlanned) } : {}),
    ...(input.status !== undefined ? { status: input.status } : {}),
    ...(input.vendor !== undefined ? { vendor: input.vendor } : {}),
    ...(input.invoiceNo !== undefined ? { invoice_no: input.invoiceNo } : {}),
    ...(input.documentUrl !== undefined ? { document_url: input.documentUrl } : {}),
    ...(input.recurrenceId !== undefined ? { cost_recurrence_id: input.recurrenceId } : {}),
  };
}

/** A cost has no single naming field, so the log gets the title and the amount together. */
function costLabel(input: Partial<CostInput>): string {
  const amount = input.amount == null ? "" : toRemoteAmount(input.amount);
  return [input.title?.trim(), amount].filter(Boolean).join(" · ");
}

export async function listCosts(filters: CostFilters = {}): Promise<Cost[]> {
  const response = await apiRequest<ListResponse<RemoteCost>>(
    `/api/costs${costFiltersToQuery(filters)}`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCost);
}

export async function getCost(id: string): Promise<Cost> {
  const response = await apiRequest<{ success: true; data: RemoteCost }>(`/api/costs/${id}`, {
    method: "GET",
    cache: "no-store",
  });
  return mapCost(response.data);
}

/** Resolves to the new id — upstream returns nothing else, so a follow-up PUT needs it. */
export async function createCost(input: CostInput): Promise<string> {
  const response = await apiRequest<{ success: true; id: string }>("/api/costs", {
    method: "POST",
    body: JSON.stringify(toRemoteCost(input)),
  });
  logCrud("create", "cost", costLabel(input), response.id);
  return response.id;
}

/**
 * Partial by contract — upstream wants only the fields the user changed
 * (`docs/COSTS_API_SERVER.md`), so callers pass a diff, never the loaded record.
 */
export async function updateCost(id: string, input: Partial<CostInput>): Promise<void> {
  await apiRequest<{ success: true }>(`/api/costs/${id}`, {
    method: "PUT",
    body: JSON.stringify(toRemoteCost(input)),
  });
  logCrud("update", "cost", costLabel(input), id);
}

/**
 * Every `done` cost is locked upstream. The one write it still accepts is this exact body, from an
 * admin, which moves it back to `in_progress` so it can be edited and closed again.
 */
export async function unlockCost(id: string, label = ""): Promise<void> {
  await apiRequest<{ success: true }>(`/api/costs/${id}`, {
    method: "PUT",
    body: JSON.stringify({ status: "in_progress" }),
  });
  logCrud("update", "cost", label, id);
}

export async function deleteCost(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/costs/${id}`, { method: "DELETE" });
  logCrud("delete", "cost", "", id);
}

export async function listCostLog(costId: string): Promise<CostLogEntry[]> {
  const response = await apiRequest<ListResponse<RemoteCostLogEntry>>(
    `/api/costs/${costId}/log`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCostLogEntry);
}
